import { Trans, useLingui } from "@lingui/react/macro";
import { AccessibleIcon } from "@offstreetparking/design-system/accessibility";
import { formatDate } from "@offstreetparking/design-system/formatters";
import { HashIcon } from "@offstreetparking/design-system/icons/HashIcon";
import { ReceiptIcon } from "@offstreetparking/design-system/icons/ReceiptIcon";
import { useLocale } from "@offstreetparking/design-system/providers";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import { PaymentScheduleOverview, ProrationInfoBanner } from "../../parkingDetails";
import { PriceBreakdown } from "../../parkingDetails/PriceBreakDown";
import type { ProrationInterval } from "../../parkingDetails/getProration";
import type { Permit } from "../types";
type InvoiceSectionProps = {
	invoice?: Permit["invoice"];
	paymentSchedule?: Permit["paymentSchedule"];
	createdDate: Permit["createdDate"];
	timezone: Permit["timezone"];
	company: Permit["company"];
	email?: string;
	fullTermIntervals?: ProrationInterval[];
};
export const InvoiceSection = ({
	invoice,
	paymentSchedule,
	createdDate,
	timezone,
	company,
	email,
	fullTermIntervals,
}: InvoiceSectionProps) => {
	const { t } = useLingui();
	const { locale } = useLocale();
	const isPaid = (invoice?.total ?? 0) > 0;
	if (!isPaid && !paymentSchedule) {
		return null;
	}
	return (
		<div className="mt-4 border-neutral-faded border-t pt-6">
			<Heading As="h3" kind="h3">
				{t`Payment Summary`}
			</Heading>
			{paymentSchedule && (
				<>
					<div className="mt-4 rounded-md bg-info-faded p-3">
						<Text size="sm">{t`Your permit will be paid through automatic payroll deductions.`}</Text>
					</div>
					<div className="mt-4">
						<PaymentScheduleOverview
							overview={paymentSchedule}
							country={company.address?.country ?? undefined}
						/>
					</div>
				</>
			)}
			{invoice && isPaid && (
				<>
					{invoice.discountItems?.some((item) => item.type === "Proration") && (
						<div className="mt-4">
							<ProrationInfoBanner
								fullTermIntervals={fullTermIntervals}
								timezone={timezone ?? undefined}
							/>
						</div>
					)}
					<div className="mt-4 flex items-center gap-2">
						<AccessibleIcon label={t`Payment date`}>
							<ReceiptIcon size={16} />
						</AccessibleIcon>
						<Text size="sm">
							{formatDate({
								date: createdDate,
								formatType: "Abbreviated_DateTime",
								timezone,
								language: locale,
							})}
						</Text>
					</div>
					<div className="mt-2 flex items-center gap-2">
						<AccessibleIcon
							label={t({
								message: "Receipt Number",
								comment:
									"Label for the unique reference of a payment receipt, e.g. RCT_RZ9YWB3R5, that a parker quotes to parking admins",
							})}
						>
							<HashIcon size={16} />
						</AccessibleIcon>
						<Text size="sm">{invoice.receiptIdentifier}</Text>
					</div>
					<PriceBreakdown
						invoice={invoice}
						country={company.address?.country ?? undefined}
						timezone={timezone}
					/>
					{email && (
						<div className="flex gap-1 text-sm">
							<Trans>
								Receipt emailed to: <b className="font-semibold">{email}</b>
							</Trans>
						</div>
					)}
				</>
			)}
		</div>
	);
};
